import { Button, Tooltip, useToast } from '@chakra-ui/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { funding as calcAgentFunding } from '@finley-technologies/calc-agent';
import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  DbTable,
  Loan,
  Reporting,
  Util,
} from '@finley-technologies/finley-types';

import {
  FundingRequestContextValue,
  useFundingRequestTemplateContextV1,
} from '../../hooks/useFundingRequestTemplateContextV1';
import { Icon } from 'packages/common-frontend/components/icons';
import { errorToastConfig } from 'packages/common-frontend/constants';
import {
  useGetReportS3DownloadUrlByReportingWorkleaseRecordIdLazyQueryGQLType,
  useProduceReportingGenerateMessageMutation,
} from 'packages/reporting/requests/reporting.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

type GeneratePreviewFundingNoticeWorkleaseParams = {
  reportingDate: Util.FullDateStr;
  lenderShareSubmission: calcAgentFunding.DrawRequestSubmissionLenderShare;
  setIsLoading: (isLoading: boolean) => void;
  fundingRequestContext: FundingRequestContextValue;
  targetFundingDate: Util.FullDateStr;
  pricingOptionKey: string;
  totalDrawRequestProjectedInterest: number;
  reportName: string;
  userOrganizationId: string;
};

export const generatePreviewFundingNoticeWorklease = ({
  reportingDate,
  lenderShareSubmission,
  fundingRequestContext,
  targetFundingDate,
  pricingOptionKey,
  totalDrawRequestProjectedInterest,
  reportName,
  userOrganizationId,
}: GeneratePreviewFundingNoticeWorkleaseParams): Reporting.FundingNoticeReportingGenerateQaWorklease => {
  const {
    creditFacility: {
      id: creditFacilityId,
      name: creditFacilityName,
      borrowerId,
      borrowerEntityName,
    },
    context: {
      creditAgreementId,
      summary: { commitment },

      data: {
        expectedSubmission: { draw },
        maturityDate,
        externalLoanId,
        loanType,
      },
    },
  } = fundingRequestContext;

  const {
    global: {
      targetFundingDate: globalTargetFundingDate,
      requestAmount: globalDrawRequestAmount,
    },
  } = draw;

  const {
    amount,
    percent,
    lender: {
      organizationId: lenderOrganizationId,
      name: lenderOrganizationName,
    },
    projectedInterest: lenderShareProjectedInterest,
    fundingRequestComponentId: lenderShareFundingRequestComponentId,
  } = lenderShareSubmission;

  const previewLenderShareRecord: DbTable.CreditFacilityLenderShareRecord = {
    id: '',
    borrowerId,
    capitalProviderId: lenderOrganizationId,
    loanId: '',
  };

  const previewResolvedLenderShare: Loan.ResolvedLenderShare = {
    lenderOrganization: {
      id: lenderOrganizationId,
      name: lenderOrganizationName,
      slug: '',
    },
    lenderShareId: '',
    allocationPercent: percent,
    initialFundingAmount: amount,
    outstandingBalance: amount,
    totalAccruedInterest: 0, // the share has no accrued interest at this point
    projectedInterest: lenderShareProjectedInterest,
  };

  const selectedDraw =
    draw.requests.find((req) => req.amount > 0) ?? draw.requests[0];
  const commitmentKey = selectedDraw.key;

  const previewLoan: Omit<Loan.ResolvedLoan, 'pricing' | 'invoiceMechanics'> = {
    id: 'Preview',
    externalLoanId: externalLoanId ?? null,

    name: 'Preview',
    commitmentKey,
    memo: null,
    loanStatus: 'initiated',
    calculatedLoanStatus: 'initiated',
    loanType,
    isFinal: true,

    metadata: {
      loanKey: selectedDraw.loanKey,
      pricingOptionKey: selectedDraw.pricingOptionKey,
    },
    edits: {
      source: null,
      destination: null,
    },
    dates: {
      originationDate: targetFundingDate ?? globalTargetFundingDate,
      targetFundingDate: targetFundingDate ?? globalTargetFundingDate,
      maturityDate,
      settledDate: null,
    },

    facility: {
      commitmentAmount: commitment.amount,
      creditFacilityId,
      creditFacilityName,
      externalFacilityId: null,
    },

    parties: {
      borrowerOrganization: {
        id: borrowerId,
        name: borrowerEntityName,
        slug: '',
      },
      adminAgentOrganization: null,
      lenderOrganizations: [],
    },

    funding: {
      commitmentKey,
      initialFundingAmount: globalDrawRequestAmount,
      totalOutstandingBalance: globalDrawRequestAmount, // this is the preview outstanding balance for the loan, if the loan were to fund
      lenderShares: [previewResolvedLenderShare],
      requests: [],
    },

    fees: {
      accruedInterest: {
        // TODO: no accrued interest at this point @markhaghani @sachinr4
        totalAmount: 0,
        annualAmounts: [],
      },
      projectedInterest: totalDrawRequestProjectedInterest,
      accruedServiceFees: {
        // TODO: no accrued service fees at this point @markhaghani @sachinr4
        totalAmount: 0,
        annualAmounts: [],
      },
      projectedServiceFees: 0, // @dani todo - update with projected service fees calc from rate when projected service fees is implemented
      totalAccruedInterestAndFees: {
        totalAmount: 0,
        annualAmounts: [],
      },
    },
    invoices: {
      nextPaymentDate: null,
      nextInvoiceId: null,
      mostRecentPaymentDate: null,
    },
  };

  const worklease: Reporting.FundingNoticeReportingGenerateQaWorklease = {
    initiatingOrganizationId: userOrganizationId,
    creditFacilityId,
    creditAgreementId,
    reportName,
    reportingDate,
    mode: 'QA',
    reportTemplateFileType: 'docx',
    reportConfigType: 'fundingNotice',
    config: {
      lenderShareId: null,
      fundingRequestComponentId: lenderShareFundingRequestComponentId,
      previewLenderShare: previewLenderShareRecord,
      previewLoan,
      pricingOptionKey,
    },
  };

  return worklease;
};

type DownloadFundingNoticeButtonBodyParams = {
  reportName: string;
  targetFundingDate: Util.FullDateStr;
  lenderShareSubmission: calcAgentFunding.DrawRequestSubmissionLenderShare;
  pricingOptionKey: string;
  totalDrawRequestProjectedInterest: number;
};
export const DownloadFundingNoticeButtonBody = ({
  reportName,
  lenderShareSubmission,
  targetFundingDate,
  pricingOptionKey,
  totalDrawRequestProjectedInterest,
}: DownloadFundingNoticeButtonBodyParams) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();

  const fundingRequestContext = useFundingRequestTemplateContextV1();

  const [
    currentPollingWorkleaseRecordId,
    setCurrentPollingWorkleaseRecordId,
  ] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [produceGenerateMessage] = useProduceReportingGenerateMessageMutation({
    fetchPolicy: 'no-cache',
  });

  const [
    getReportS3DownloadUrlByWorklease,
    { data: reportS3DownloadUrlByWorkleaseData },
  ] = useGetReportS3DownloadUrlByReportingWorkleaseRecordIdLazyQueryGQLType({
    pollInterval: currentPollingWorkleaseRecordId ? 1000 : 0, // poll every second if 'currentPollingReport' exists
    fetchPolicy: 'no-cache',
  });

  const generatePreviewFundingNotice = useCallback(
    async ({ reportingDate }: { reportingDate: Util.FullDateStr }) => {
      setIsLoading(true);

      const worklease = await generatePreviewFundingNoticeWorklease({
        reportingDate,
        lenderShareSubmission,
        targetFundingDate,
        pricingOptionKey,
        totalDrawRequestProjectedInterest,
        reportName,
        userOrganizationId: user.organizationId,
        setIsLoading,
        fundingRequestContext,
      });

      const { data } = await produceGenerateMessage({
        variables: {
          worklease,
          parameters: null,
        },
      });
      finleyAssert(
        data?.result,
        'Expecting a result from "produceGenerateMessage".',
      );

      setCurrentPollingWorkleaseRecordId(data.result.recordId);

      getReportS3DownloadUrlByWorklease({
        variables: {
          reportConfigType: 'fundingNotice',
          reportingWorkleaseRecordId: data.result.recordId,
        },
      });
    },
    [
      produceGenerateMessage,
      user.organizationId,
      reportName,
      getReportS3DownloadUrlByWorklease,
      lenderShareSubmission,
      targetFundingDate,
      pricingOptionKey,
      totalDrawRequestProjectedInterest,
      fundingRequestContext,
    ],
  );

  useEffect(() => {
    const handleWorkleaseStatus = async () => {
      // if worklease is initialized state, return and continue polling
      if (
        !reportS3DownloadUrlByWorkleaseData?.getReportS3DownloadUrlByReportingWorkleaseRecordId ||
        reportS3DownloadUrlByWorkleaseData
          .getReportS3DownloadUrlByReportingWorkleaseRecordId
          .workleaseStatus === 'initialized'
      ) {
        return;
      }

      const {
        getReportS3DownloadUrlByReportingWorkleaseRecordId: {
          workleaseStatus,
          s3DownloadUrl,
          error,
        },
      } = reportS3DownloadUrlByWorkleaseData;

      // if worklease is in error state, toast error and stop polling
      if (workleaseStatus === 'error' || !s3DownloadUrl) {
        const errorToastDescription =
          commonUtils.EnvUtils.isProduction() || !error ? undefined : error;

        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URL [report:${reportName}]`,
          description: errorToastDescription,
        });

        setIsLoading(false);
        setCurrentPollingWorkleaseRecordId(undefined);
        return;
      }

      // if worklease is in success state, download file from S3 url and stop polling
      if (workleaseStatus === 'success') {
        setCurrentPollingWorkleaseRecordId(undefined);

        try {
          const response = await axios({
            url: s3DownloadUrl,
            method: 'GET',
            responseType: 'blob',
          });

          const fileBlob = new Blob([response.data], {
            type: response.data.type,
          });
          const fileName = `${reportName}-preview-${commonDate.currentDateStr()}`;
          FileSaver.saveAs(fileBlob, fileName);
        } catch (err) {
          errorToastConfig.title = commonUtils.EnvUtils.isProduction()
            ? 'Failed to retrieve report preview. Please contact Finley for support.'
            : JSON.stringify(err);

          toast(errorToastConfig);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (currentPollingWorkleaseRecordId) {
      // if there is a worklease we are currently polling for,
      // handle the worklease results
      handleWorkleaseStatus();
    }
  }, [
    reportName,
    reportS3DownloadUrlByWorkleaseData,
    toast,
    currentPollingWorkleaseRecordId,
  ]);

  // @dani replace this mock reporting date with the actual reporting date of the borrowing base report being used for the funding request once real data is hooked up
  const reportingDate = useMemo(() => {
    return commonDate.currentDateStr();
  }, []);

  return (
    <Tooltip label="Generate and download notice preview">
      <Button
        variant="v2-ghost"
        onClick={async () => {
          await generatePreviewFundingNotice({
            reportingDate,
          });
        }}
        isLoading={isLoading}
      >
        <Icon icon="arrow-down-to-bracket" />
      </Button>
    </Tooltip>
  );
};

type DownloadFundingNoticeButtonParams = {
  reportName: string;
  targetFundingDate: Util.FullDateStr;
  lenderShareSubmission: calcAgentFunding.DrawRequestSubmissionLenderShare;
  pricingOptionKey: string;
  totalDrawRequestProjectedInterest: number;
};
export const DownloadPreviewFundingNoticeButton = ({
  reportName,
  lenderShareSubmission,
  targetFundingDate,
  pricingOptionKey,
  totalDrawRequestProjectedInterest,
}: DownloadFundingNoticeButtonParams) => {
  return (
    <>
      {reportName ? (
        <DownloadFundingNoticeButtonBody
          lenderShareSubmission={lenderShareSubmission}
          reportName={reportName}
          targetFundingDate={targetFundingDate}
          pricingOptionKey={pricingOptionKey}
          totalDrawRequestProjectedInterest={totalDrawRequestProjectedInterest}
        />
      ) : (
        <Tooltip label="Funding notice is not configured">
          <Button isDisabled variant="v2-ghost">
            <Icon icon="arrow-down-to-bracket" />
          </Button>
        </Tooltip>
      )}
    </>
  );
};
